import {Service, Brick, registerBrick, ServiceRequestType} from 'olympe';
import {TimeoutMap} from '@olympeio/core';
import {tap} from 'rxjs/operators';
import {
    applyAllRaPeerSnapshots,
    collectPeerSnapshots,
    COLLECT_MEMORIES_TIMEOUT_MS,
    createSyncMutationLog,
    logSharedMemorySync,
    MAX_EXPECTED_REPLICAS,
    noteOwnMulticast,
    recordCollectReply,
    replayAllRaMutations,
    SYNC_ECHO_GRACE_MS,
    SYNC_MUTATION,
} from './sharedMemorySync';

/**
 * A Brick that manages shared memory for all RA services.
 * This shared memory ensures consistent internal state across multiple backends for specific session data handling.
 */
export default class CreateAllRASharedMemory extends Brick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array} inputs
     * @param {function()} forwardEvent
     */
    async update($, inputs, [forwardEvent]) {
        const backendId = crypto.randomUUID();

        const allRaSharedMemory = new Service('allRaSharedMemory', $);

        // This timeoutMap contains the session id for Candex API
        // The key is the user Id, and the value is the session id
        // Timeout is 30 minutes, refresh rate 5 minutes
        const candexSessionIds = /** @type {TimeoutMap<string, string>} */ new TimeoutMap(30 * 60 * 1000, 5 * 60 * 1000);

        // This timeoutMap contains the session info for NestBuy Catalogs
        // The key is the OCI session id, and the value is an object with different information, including the session transmission Id
        // Timeout is 30 minutes, refresh rate 5 minutes
        const nestBuySession = /** @type {TimeoutMap<string, {}>} */ new TimeoutMap(30 * 60 * 1000, 5 * 60 * 1000);

        // This timeoutMap contains the buyer cookies for cXML communication (PunchOut catalogs)
        // The key is the buyer cookie id, and the value is the buyer cookie data
        // Timeout is 30 minutes, refresh rate 5 minutes
        const cxmlBuyerCookies = /** @type {TimeoutMap<string, string>} */ new TimeoutMap(30 * 60 * 1000, 5 * 60 * 1000);

        // This Map contains the system alias for a user
        const systemAliasByUser = /** @type {Map<string, string>} */ new Map();

        // Scatter-gather collections in progress, when this backend acts as coordinator for ALL_MEMORIES_ALL_BACKENDS.
        // The key is the correlation id of the collection, the value holds the replies received so far and a `done` callback.
        const pendingCollections = /** @type {Map<string, {results: Map<string, Object>, expected: number, done: function()}>} */ new Map();

        // Live mutations recorded during startup sync.
        const startupMutations = createSyncMutationLog();
        startupMutations.start();

        /**
         * Build a snapshot of every memory held by this backend.
         *
         * @return {{backendId: string, candexSessionIds: Map<string, string>, nestBuySessionInfo: Map<string, Object>, cxmlBuyerCookies: Map<string, Object>, systemAliasByUser: Map<string, string>}}
         */
        const getMemoriesSnapshot = () => ({
            backendId: backendId,
            candexSessionIds: candexSessionIds.values,
            nestBuySessionInfo: nestBuySession.values,
            cxmlBuyerCookies: cxmlBuyerCookies.values,
            systemAliasByUser: systemAliasByUser,
        });

        /**
         * Collect the memories snapshot of every running backend (scatter-gather).
         * This backend acts as coordinator: it multicasts a COLLECT_MEMORIES request, every other backend answers
         * with a multicast COLLECT_MEMORIES_REPLY, and the replies are gathered here until `expectedReplicas`
         * backends have answered (this one included) or COLLECT_MEMORIES_TIMEOUT_MS elapsed.
         *
         * @param {number} expectedReplicas number of backends expected to answer, this one included
         * @return {Promise<Map<string, Object>>} the snapshots, keyed by backend id
         */
        const collectAllBackendsMemories = (expectedReplicas) => collectPeerSnapshots({
            multicast: (body) => allRaSharedMemory.multicast(body),
            requestBody: {topic: SHARED_MEMORY_TOPICS.COLLECT_MEMORIES},
            selfId: backendId,
            selfSnapshot: getMemoriesSnapshot(),
            expected: expectedReplicas,
            timeoutMs: COLLECT_MEMORIES_TIMEOUT_MS,
            pendingCollections,
        });

        allRaSharedMemory.listen().pipe(
            tap(async (req) => {
                const {topic,
                    uuid,
                    userId,
                    buyerCookieId,
                    buyerCookie,
                    sessionId,
                    ociSessionId,
                    nestBuySessionInfo,
                    systemAlias,
                    expectedReplicas,
                    correlationId,
                    coordinatorId,
                    memories,
                } = req.body();

                switch (req.requestType()) {
                case ServiceRequestType.GET:

                    switch (topic) {
                    case SHARED_MEMORY_TOPICS.CANDEX_SESSION_ID:
                        req.reply({sessionId: candexSessionIds.get(userId)});
                        break;
                    case SHARED_MEMORY_TOPICS.NESTBUY_SESSION_INFO:
                        req.reply({sessionInfo: nestBuySession.get(ociSessionId)});
                        break;
                    case SHARED_MEMORY_TOPICS.CXML_BUYER_COOKIE:
                        req.reply({buyerCookie: cxmlBuyerCookies.get(buyerCookieId)});
                        break;
                    case SHARED_MEMORY_TOPICS.CXML_BUYER_COOKIE_EXISTS:
                        req.reply({exists: cxmlBuyerCookies.has(buyerCookieId)});
                        break;
                    case SHARED_MEMORY_TOPICS.USER_SYSTEM_ALIAS:
                        req.reply({systemAlias: systemAliasByUser.get(userId)});
                        break;
                    case SHARED_MEMORY_TOPICS.ALL_MEMORIES:
                        console.log('ALL_MEMORIES');
                        req.reply(getMemoriesSnapshot());
                        break;
                    case SHARED_MEMORY_TOPICS.ALL_MEMORIES_ALL_BACKENDS: {
                        // Sanitize the hint coming from the frontend: at least this backend, at most a sane upper bound.
                        const expected = Math.min(Math.max(Math.floor(Number(expectedReplicas)) || 1, 1), MAX_EXPECTED_REPLICAS);
                        const backends = await collectAllBackendsMemories(expected);
                        req.reply({
                            coordinatorId: backendId,
                            expectedReplicas: expected,
                            complete: backends.size >= expected,
                            backends: backends,
                        });
                        break;
                    }
                    }

                    break;
                case ServiceRequestType.PUBLISH:
                    if (uuid === backendId) {
                        // Own multicast: record the echo and do not apply it.
                        noteOwnMulticast(pendingCollections, correlationId);
                        return;
                    }

                    switch (topic) {
                    case SHARED_MEMORY_TOPICS.COLLECT_MEMORIES:
                        // A coordinator asks every backend for its memories: answer through a multicast, which is the
                        // only way to reach the coordinator (`uuid` is the coordinator id, since it multicast the request).
                        allRaSharedMemory.multicast({
                            topic: SHARED_MEMORY_TOPICS.COLLECT_MEMORIES_REPLY,
                            uuid: backendId,
                            correlationId,
                            coordinatorId: uuid,
                            memories: getMemoriesSnapshot(),
                        }).catch((e) => {
                            console.error('Error replying to the memories collection request:', e);
                        });
                        return;
                    case SHARED_MEMORY_TOPICS.COLLECT_MEMORIES_REPLY:
                        // Store this peer snapshot on the matching collection.
                        recordCollectReply(pendingCollections, backendId, {correlationId, coordinatorId, uuid, memories});
                        return;
                    case SHARED_MEMORY_TOPICS.CANDEX_SESSION_ID:
                        candexSessionIds.set(userId, sessionId);
                        startupMutations.record({op: SYNC_MUTATION.SET_CANDEX, key: userId, value: sessionId});
                        break;
                    case SHARED_MEMORY_TOPICS.NESTBUY_SESSION_INFO:
                        nestBuySession.set(ociSessionId, nestBuySessionInfo);
                        startupMutations.record({op: SYNC_MUTATION.SET_NESTBUY, key: ociSessionId, value: nestBuySessionInfo});
                        break;
                    case SHARED_MEMORY_TOPICS.NESTBUY_SESSION_INFO_DELETE:
                        nestBuySession.delete(ociSessionId);
                        startupMutations.record({op: SYNC_MUTATION.DELETE_NESTBUY, key: ociSessionId});
                        break;
                    case SHARED_MEMORY_TOPICS.CXML_BUYER_COOKIE:
                        cxmlBuyerCookies.set(buyerCookieId, buyerCookie);
                        startupMutations.record({op: SYNC_MUTATION.SET_CXML, key: buyerCookieId, value: buyerCookie});
                        break;
                    case SHARED_MEMORY_TOPICS.CXML_BUYER_COOKIE_DELETE:
                        cxmlBuyerCookies.delete(buyerCookieId);
                        startupMutations.record({op: SYNC_MUTATION.DELETE_CXML, key: buyerCookieId});
                        break;
                    case SHARED_MEMORY_TOPICS.USER_SYSTEM_ALIAS_SET:
                        systemAliasByUser.set(userId, systemAlias);
                        startupMutations.record({op: SYNC_MUTATION.SET_ALIAS, key: userId, value: systemAlias});
                        break;
                    }

                    if (!uuid) {
                        // Propagate this message to all backends to ensure everyone has a correct internal state
                        allRaSharedMemory.multicast({...req.body(), uuid: backendId}).catch((e) => {
                            console.error('Error sharing message:', e);
                        });
                    }
                    break;
                }
            })).subscribe();

        // Get data from live replicas and apply the snapshots to this replica.
        // Ensure mutations happening during startup are replayed.
        const stores = {candexSessionIds, nestBuySession, cxmlBuyerCookies, systemAliasByUser};
        try {
            const snapshots = await collectPeerSnapshots({
                multicast: (body) => allRaSharedMemory.multicast(body),
                requestBody: {topic: SHARED_MEMORY_TOPICS.COLLECT_MEMORIES},
                selfId: backendId,
                selfSnapshot: getMemoriesSnapshot(),
                expected: MAX_EXPECTED_REPLICAS,
                timeoutMs: COLLECT_MEMORIES_TIMEOUT_MS,
                peerGraceMs: SYNC_ECHO_GRACE_MS,
                pendingCollections,
            });
            const result = applyAllRaPeerSnapshots(backendId, snapshots, stores);
            replayAllRaMutations(stores, startupMutations.drain());
            logSharedMemorySync('allRaSharedMemory', result);
        } catch (e) {
            startupMutations.drain();
            console.error('Error synchronizing all RA shared memory on startup:', e);
            logSharedMemorySync('allRaSharedMemory', {peerCount: 0, applied: 0});
        }

        forwardEvent();
    }
}

export const SHARED_MEMORY_TOPICS = {
    CANDEX_SESSION_ID: 'candex_session_id',
    NESTBUY_SESSION_INFO: 'nestBuy_session_info',
    NESTBUY_SESSION_INFO_DELETE: 'nestBuy_session_info_delete',
    CXML_BUYER_COOKIE: 'cxml_buyer_cookie',
    CXML_BUYER_COOKIE_EXISTS: 'cxml_buyer_cookie_exists',
    CXML_BUYER_COOKIE_DELETE: 'cxml_buyer_cookie_delete',
    USER_SYSTEM_ALIAS: 'user_system_alias',
    USER_SYSTEM_ALIAS_SET: 'user_system_alias_set',
    ALL_MEMORIES: 'all_memories',
    ALL_MEMORIES_ALL_BACKENDS: 'all_memories_all_backends',
    COLLECT_MEMORIES: 'collect_memories',
    COLLECT_MEMORIES_REPLY: 'collect_memories_reply',
};

export {
    COLLECT_MEMORIES_TIMEOUT_MS,
    MAX_EXPECTED_REPLICAS,
} from './sharedMemorySync';

registerBrick('0197b1b20e39fa5908c7', CreateAllRASharedMemory);
